import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, MailX, XCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  confirmUnsubscribe,
  getUnsubscribeInfo,
  type UnsubscribeInfo,
} from "../api/unsubscribe";

/**
 * Publiczna strona rezygnacji (/rezygnacja/:token) — POZA ProtectedLayout.
 *
 * Bez logowania, bez menu, bez niczego z systemu. Token z maila jest jedynym
 * poświadczeniem i otwiera dokładnie jedną możliwość: wypisanie tej firmy.
 *
 * Wypisuje dopiero kliknięcie (POST). Samo wejście na stronę niczego nie zmienia,
 * bo pod ten adres wchodzą też skanery poczty i podglądy linków w klientach
 * pocztowych — gdyby wystarczyło otwarcie, wypisywalibyśmy firmy, które nigdy
 * nie kliknęły.
 */
export function UnsubscribePage() {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<UnsubscribeInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!token) return;
    getUnsubscribeInfo(token)
      .then((data) => {
        if (!alive) return;
        setInfo(data);
        if (data.already) setDone(true);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const submit = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const data = await confirmUnsubscribe(token);
      setInfo(data);
      setDone(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg-body)] flex items-center justify-center p-4">
      <div className="w-full max-w-md card p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[var(--color-bg-elevated)] flex items-center justify-center shrink-0">
            <MailX size={18} className="text-[var(--color-primary)]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-semibold text-[var(--color-text-primary)] leading-tight">
              Rezygnacja z kontaktu
            </h1>
            <p className="text-[11px] text-[var(--color-text-muted)] uppercase tracking-wide">
              GK ELEKTIM
            </p>
          </div>
        </div>

        {!info && !error && (
          <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] py-4">
            <Loader2 size={15} className="animate-spin" />
            Wczytywanie…
          </div>
        )}

        {error && !info && (
          <div className="flex items-start gap-3 text-sm">
            <XCircle size={18} className="text-[var(--color-primary)] shrink-0 mt-0.5" />
            <div>
              <p className="text-[var(--color-text-primary)] font-medium">
                Nie udało się otworzyć tego linku
              </p>
              <p className="text-[var(--color-text-secondary)] mt-1">{error}</p>
              <p className="text-[var(--color-text-muted)] mt-3 text-[13px]">
                Proszę odpisać na naszą wiadomość — wypiszemy Państwa ręcznie.
              </p>
            </div>
          </div>
        )}

        {info && done && (
          <div className="flex items-start gap-3 text-sm">
            <CheckCircle2 size={18} className="text-[var(--color-primary)] shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[var(--color-text-primary)] font-medium">
                Gotowe — nie napiszemy ponownie
              </p>
              <p className="text-[var(--color-text-secondary)] mt-1 break-words">
                Firma{" "}
                <span className="text-[var(--color-text-primary)] font-medium">
                  {info.company}
                </span>{" "}
                została usunięta z naszej listy kontaktowej. Zaplanowane wiadomości
                zostały zatrzymane.
              </p>
              <p className="text-[11px] text-[var(--color-text-muted)] mt-3 break-words">
                Dotyczy adresu {info.email} oraz pozostałych danych kontaktowych tej firmy.
              </p>
            </div>
          </div>
        )}

        {info && !done && (
          <>
            <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
              Po potwierdzeniu usuniemy dane kontaktowe firmy{" "}
              <span className="text-[var(--color-text-primary)] font-medium break-words">
                {info.company}
              </span>{" "}
              z naszej listy i nie wyślemy już żadnej wiadomości — również tych
              zaplanowanych.
            </p>
            <p className="text-[11px] text-[var(--color-text-muted)] mt-3 break-words">
              Adres, którego dotyczy: {info.email}
            </p>

            {error && (
              <p className="text-sm text-[var(--color-primary)] mt-4">{error}</p>
            )}

            <Button className="w-full mt-6" loading={busy} onClick={submit}>
              Nie chcę otrzymywać wiadomości
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
